import React from 'react';
import classNames from 'classnames';
import { i18n } from 'config/locales';
import { AlternateAddress } from 'components/Form/Location/AlternateAddress';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonAddressBooks,
  CommonScrollIntoView,
  CommonRequired,
  CommonOnError,
  CommonOnUpdate,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import {
  ApplicableModel,
  CountryArrayModel, CountryModel, DateModel, LocationModel, NameModel
} from 'forms/shared/types';
import { Dispatch } from 'redux';
import { ForeignContactsItemModel } from 'forms/sections/foreign/contacts/types';
import Checkbox from 'components/Form/Checkbox';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import NotApplicable from 'components/Form/NotApplicable';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import BranchCollection from 'components/Form/BranchCollection';
import Country from 'components/Form/Country';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import { isPVQForm } from 'helpers/form';
import PVQName from 'components/Form/PVQName';
import { otherNamesOptions } from 'constants/enums/relationshipOptions';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import {
  foreignContactFrequencyOptions, foreignContactMethodOptions, foreignContactRelationshipOptions,
  foreignNationalWorkOptions
} from 'constants/enums/foreignActivityOptions';
import Telephone from 'components/Form/Telephone';
import Branch from 'components/Form/Branch';
import PVQEmail from 'components/Form/PVQEmail';
import { isSafeInternational } from 'helpers/locations';
import { countryString } from '../../../../validators/location';
import { pickDate } from '../../../../validators/helpers';
import { ForeignContactsItemAliasesModel } from '../../../../forms/sections/foreign/contacts/types';

type UpdateField<Model extends {}> = <Key extends keyof Model>(
  field: Key,
  values: Model[Key]
) => void

interface Props extends Partial<ForeignContactsItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  applicantBirthdate: DateModel
  requireForeignContactAPOFPOIDK: boolean
  dispatch: Dispatch
  formType?: string
}

export default class ForeignNational extends ValidationElement<Props> {
  constructor(props) {
    super(props);

    this.state = {
      currentAddressIdkRefresh: 0,
    };
  }

  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignContactsItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      NameNotApplicable: this.props.NameNotApplicable,
      NameExplanation: this.props.NameExplanation,
      FirstContact: this.props.FirstContact,
      LastContact: this.props.LastContact,
      Methods: this.props.Methods,
      MethodsExplanation: this.props.MethodsExplanation,
      Frequency: this.props.Frequency,
      FrequencyExplanation: this.props.FrequencyExplanation,
      Relationship: this.props.Relationship,
      RelationshipExplanation: this.props.RelationshipExplanation,
      Aliases: this.props.Aliases,
      Citizenship: this.props.Citizenship,
      Birthdate: this.props.Birthdate,
      BirthdateNotApplicable: this.props.BirthdateNotApplicable,
      Birthplace: this.props.Birthplace,
      BirthplaceNotApplicable: this.props.BirthplaceNotApplicable,
      Address: this.props.Address,
      AddressNotApplicable: this.props.AddressNotApplicable,
      InUsaNotApplicable: this.props.InUsaNotApplicable, // PVQ Only
      Employer: this.props.Employer,
      EmployerNotApplicable: this.props.EmployerNotApplicable,
      EmployerAddress: this.props.EmployerAddress,
      EmployerAddressNotApplicable: this.props.EmployerAddressNotApplicable,
      BirthplaceExplanation: this.props.BirthplaceExplanation,
      HasAffiliations: this.props.HasAffiliations,
      Affiliations: this.props.Affiliations,
      AlternateAddress: this.props.AlternateAddress,
      HowCitizenshipAcquired: this.props.HowCitizenshipAcquired, // PVQ Only
      HowCitizenshipAcquiredNotApplicable: this.props.HowCitizenshipAcquiredNotApplicable, // PVQ Only
      RelationshipPvq: this.props.RelationshipPvq, // PVQ Only
      Email: this.props.Email, // PVQ Only
      EmailNotApplicable: this.props.EmailNotApplicable, // PVQ Only
      Telephone: this.props.Telephone, // PVQ Only
      Occupation: this.props.Occupation, // PVQ Only
      OccupationNotApplicable: this.props.OccupationNotApplicable, // PVQ Only
      ForeignDisciplines: this.props.ForeignDisciplines, // PVQ Only
      ForeignDisciplinesOtherExplanation: this.props.ForeignDisciplinesOtherExplanation, // PVQ Only
      ForeignDisciplinesNotApplicable: this.props.ForeignDisciplinesNotApplicable, // PVQ Only
      ForeignAssociationDescription: this.props.ForeignAssociationDescription, // PVQ Only
      ForeignAssociationDescriptionNotApplicable: this.props.ForeignAssociationDescriptionNotApplicable, // PVQ Only
      HasObligations: this.props.HasObligations, // PVQ Only
      Obligations: this.props.Obligations, // PVQ Only
      NeverWorked: this.props.NeverWorked, // PVQ Only
      ...queue,
    });
  };

  updateField: UpdateField<ForeignContactsItemModel> = (field, values) => {
    this.update({
      [field]: values,
    });
  };

  updateFrequency = (values: ForeignContactsItemModel['Frequency']) => {
    this.update({
      Frequency: values,
      FrequencyExplanation: values.value === 'Other' ? this.props.FrequencyExplanation : {}
    });
  };

  updateAddressField = (values: ForeignContactsItemModel['Address']) => {
    if (countryString(values.country) === 'United States') {
      this.update({
        AlternateAddress: {},
        Address: values,
      });
    } else {
      this.update({
        Address: values,
      });
    }
  };

  updateName = (value: ForeignContactsItemModel['Name']) => {
    this.update({
      Name: value,
    });
  };

  updateNameNotApplicable = (value: ForeignContactsItemModel['NameNotApplicable']) => {
    this.update({
      Name: value.applicable ? this.props.Name : {},
      NameNotApplicable: value,
    });
  };

  updateNameExplanation = (value: ForeignContactsItemModel['NameExplanation']) => {
    this.updateField('NameExplanation', value);
  };

  updateCitizenship = (value: ForeignContactsItemModel['Citizenship']) => {
    this.update({
      Citizenship: value,
    });
  };

  updateBirthdate = (value: ForeignContactsItemModel['Birthdate']) => {
    this.update({
      Birthdate: value,
    });
  };

  updateBirthdateNotApplicable = (value: ForeignContactsItemModel['BirthdateNotApplicable']) => {
    this.update({
      Birthdate: value.applicable ? this.props.Birthdate : { month: '', day: '', year: '' },
      BirthdateNotApplicable: value,
    });
  };

  updateBirthplace = (value: ForeignContactsItemModel['Birthplace']) => {
    this.update({
      Birthplace: value,
    });
  };

  updateBirthplaceNotApplicable = (value: ForeignContactsItemModel['BirthplaceNotApplicable']) => {
    this.update({
      Birthplace: value.applicable ? this.props.Birthplace : {},
      BirthplaceNotApplicable: value,
      BirthplaceExplanation: value.applicable ? this.props.BirthplaceExplanation : {},
    });
  };

  updateBirthplaceExplanation = (value: ForeignContactsItemModel['BirthplaceExplanation']) => {
    this.updateField('BirthplaceExplanation', value);
  };

  updateHowCitizenshipAcquired = (value: ForeignContactsItemModel['HowCitizenshipAcquired']) => {
    this.update({
      HowCitizenshipAcquired: value,
    });
  };

  updateHowCitizenshipAcquiredNotApplicable = (value: ForeignContactsItemModel['HowCitizenshipAcquiredNotApplicable']) => {
    this.update({
      HowCitizenshipAcquired: value.applicable ? this.props.HowCitizenshipAcquired : {},
      HowCitizenshipAcquiredNotApplicable: value
    });
  };

  updateRelationshipPvq = (value: ForeignContactsItemModel['RelationshipPvq']) => {
    this.update({
      RelationshipPvq: value,
    });
  };

  updateRelationshipExplanation = (value: ForeignContactsItemModel['RelationshipExplanation']) => {
    this.update({
      RelationshipExplanation: value
    });
  };

  updateEmployer = (value: ForeignContactsItemModel['Employer']) => {
    this.update({
      Employer: value,
      NeverWorked: { ...this.props.NeverWorked, checked: false }
    });
  };

  updateEmployerNotApplicable = (value: ForeignContactsItemModel['EmployerNotApplicable']) => {
    this.update({
      Employer: value?.applicable ? this.props.Employer : {},
      NeverWorked: value?.applicable ? this.props.NeverWorked : {},
      EmployerNotApplicable: value,
    });
  };

  updateNeverWorked = (value: ForeignContactsItemModel['NeverWorked']) => {
    this.update({
      Employer: value?.checked ? {} : this.props.Employer,
      NeverWorked: value
    });
  };

  updateOccupation = (value: ForeignContactsItemModel['Occupation']) => {
    this.update({
      Occupation: value,
    });
  };

  updateOccupationNotApplicable = (value: ForeignContactsItemModel['OccupationNotApplicable']) => {
    this.update({
      Occupation: value?.applicable ? this.props.Occupation : {},
      OccupationNotApplicable: value,
    });
  };

  updateEmployerAddress = (
    values: ForeignContactsItemModel['EmployerAddress']
  ) => {
    this.update({ EmployerAddress: values });
  };

  updateEmployerAddressNotApplicable = (
    value: ForeignContactsItemModel['EmployerAddressNotApplicable']
  ) => {
    this.update({
      EmployerAddress: value.applicable ? this.props.EmployerAddress : {},
      EmployerAddressNotApplicable: value,
    });
  };

  updateAddressNotApplicable = (value: ForeignContactsItemModel['AddressNotApplicable']) => {
    if (value?.applicable === false
      && (this.props.Address
        && (this.props.Address.city
          || this.props.Address.zipcode
          || this.props.Address.street
          || this.props.Address.state
          || (
            this.props.Address.country
            && this.props.Address.country.value !== 'United States'
            && this.props.Address.country.value !== 'INTERNATIONAL'
          )))) {
      if (window?.confirm(i18n.t('foreign.contacts.label.confirmation')) === true) {
        this.update({
          Address: value.applicable ? this.props.Address : {},
          AddressNotApplicable: value,
        });
      } else {
        this.update({
          AddressNotApplicable: { applicable: true },
        });
        this.setState({ currentAddressIdkRefresh: Date.now() });
      }
    } else {
      this.update({
        Address: value.applicable ? this.props.Address : {},
        AddressNotApplicable: value,
      });
    }
  };

  updateAddress = (
    values: ForeignContactsItemModel['Address']
  ) => {
    this.update({ Address: values });
  };

  updateAddressNotApplicablePvq = (
    addressNotApplicable: ForeignContactsItemModel['AddressNotApplicable']
  ) => {
    const queue: Partial<ForeignContactsItemModel> = { AddressNotApplicable: addressNotApplicable };

    if (addressNotApplicable.applicable === false) {
      const address: LocationModel | undefined = this.props.Address;

      let country: CountryModel | string | CountryArrayModel | undefined = address?.country;

      if (country) {
        if (isSafeInternational(address)) {
          country = { value: 'INTERNATIONAL' } as CountryModel;
        }

        queue.Address = { country } as LocationModel;
      } else {
        queue.Address = {} as LocationModel;
      }
    }

    this.update(queue);
  };

  updateInUsaNotApplicable = (
    inUsaNotApplicable: ForeignContactsItemModel['InUsaNotApplicable']
  ) => {
    const queue: Partial<ForeignContactsItemModel> = {
      InUsaNotApplicable: { applicable: inUsaNotApplicable.applicable }
    };

    if (!inUsaNotApplicable.applicable) {
      queue.Address = {} as LocationModel;
    }

    this.update(queue);
  };

  updateMethods = (values: ForeignContactsItemModel['Methods']) => {
    const list = Checkbox.select(values, this.props.Methods);
    const includesOther = list.includes('Other');
    const includesNone = list.includes('None');

    if (includesNone) {
      this.update({
        Methods: { values: Checkbox.select(values, {}) },
        MethodsExplanation: {}
      });
    } else {
      this.update({
        Methods: { values: list },
        MethodsExplanation: includesOther ? this.props.MethodsExplanation : {}
      });
    }
  };

  updateRelationship = (values: ForeignContactsItemModel['Relationship']) => {
    const list = Checkbox.select(values, this.props.Relationship);
    const includesOther = list.includes('Other') || list.includes('Obligation');
    this.update({
      Relationship: { values: list },
      RelationshipExplanation: includesOther ? this.props.RelationshipExplanation : {}
    });
  };

  updateEmailNotApplicable = (values: ForeignContactsItemModel['EmailNotApplicable']) => {
    this.update({
      EmailNotApplicable: values,
      Email: values.applicable ? this.props.Email : {},
    });
  };

  updateEmail = (values: ForeignContactsItemModel['Email']) => {
    this.update({
      Email: values,
    });
  };

  updateTelephone = (values: ForeignContactsItemModel['Telephone']) => {
    this.update({
      Telephone: values,
    });
  };

  updateForeignDisciplines = (values: ForeignContactsItemModel['ForeignDisciplines']) => {
    const list = Checkbox.select(values, this.props.ForeignDisciplines);
    const includesOther = list.includes('Other Government Agency');
    const includesNone = list.includes('They are not associated with any of the above.');

    if (includesNone) {
      this.update({
        ForeignDisciplines: { values: Checkbox.select(values, {}) },
        ForeignDisciplinesOtherExplanation: {},
      });
    } else {
      this.update({
        ForeignDisciplines: { values: list },
        ForeignDisciplinesOtherExplanation: includesOther ? this.props.ForeignDisciplinesOtherExplanation : {},
      });
    }
  };

  updateForeignDisciplinesNotApplicable = (value: ForeignContactsItemModel['ForeignDisciplinesNotApplicable']) => {
    this.update({
      ForeignDisciplines: value?.applicable ? this.props.ForeignDisciplines : {},
      ForeignDisciplinesNotApplicable: value,
    });
  };

  updateForeignDisciplinesOtherExplanation = (value: ForeignContactsItemModel['ForeignDisciplinesOtherExplanation']) => {
    this.update({
      ForeignDisciplinesOtherExplanation: value
    });
  };

  updateForeignAssociationDescription = (value: ForeignContactsItemModel['ForeignAssociationDescription']) => {
    this.update({
      ForeignAssociationDescription: value,
    });
  };

  updateForeignAssociationDescriptionNotApplicable = (value: ForeignContactsItemModel['ForeignAssociationDescriptionNotApplicable']) => {
    this.update({
      ForeignAssociationDescription: value?.applicable ? this.props.ForeignAssociationDescription : {},
      ForeignAssociationDescriptionNotApplicable: value,
    });
  };

  updateHasObligations = (values: ForeignContactsItemModel['HasObligations']) => {
    this.update({
      HasObligations: values,
    });
  };

  updateObligations = (
    values: ForeignContactsItemModel['Obligations']
  ) => {
    this.update({ Obligations: values });
  };

  render() {
    const { Methods = {} } = this.props;
    const foreignContactMinDate = pickDate([this.props.applicantBirthdate, this.props.Birthdate]);
    const attribute = `List.items[${this.props.$index}].Item`;
    const methodsPropIncludesOther = (Methods.values || []).some((x) => x === 'Other');

    const isPVQ = isPVQForm(this.props.formType);

    const birthplaceAddress:DeepSafePartial<LocationModel> = this.props.Birthplace ?? {};
    if (!birthplaceAddress?.country) {
      birthplaceAddress.country = { value: 'INTERNATIONAL' };
    }

    return isPVQ ? (
      <div className="foreign-national">
        <Field
          title={i18n.t('foreign.pvq.contacts.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          className={classNames('foreign-national-name', {
            'no-margin-bottom': !this.props.NameNotApplicable?.applicable,
          })}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Name`}
          exactAttribute
        >
          <PVQName
            {...this.props.Name}
            name="Name"
            onUpdate={this.updateName}
            aria-label="Provide the name of the individual involved in the contact"
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
            exactAttribute
            showFirstUnknown
            showMiddleUnknown
            showLastUnknown
          />
        </Field>

        <BranchCollection
          {...this.props.Aliases}
          label={i18n.t('foreign.pvq.contacts.heading.aliases')}
          appendLabel={i18n.t('foreign.pvq.contacts.heading.aliases2')}
          help="foreign.pvq.contacts.help.aliases"
          className="aliases"
          onUpdate={(value: ForeignContactsItemModel['Aliases']) => {
            this.updateField('Aliases', value);
          }}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Aliases`}
          showDNK
          exactAttribute
        >
          <ForeignNationalNameItem
            bind
            name="Item"
            handleNameError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Aliases`}
            formType={this.props.formType}
          />
        </BranchCollection>

        <Field
          title={i18n.t('foreign.pvq.contacts.heading.citizenship')}
          help="foreign.pvq.contacts.help.citizenship"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Citizenship`}
          exactAttribute
        >
          <Country
            {...this.props.Citizenship}
            name="Citizenship"
            multiple
            className="citizenship"
            onUpdate={this.updateCitizenship}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Citizenship`}
            excludeUnitedStates
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.contacts.heading.howCitizenshipAcquired')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.HowCitizenshipAcquired`}
          exactAttribute
        >
          <NotApplicable
            {...this.props.HowCitizenshipAcquiredNotApplicable}
            name="HowCitizenshipAcquiredNotApplicable"
            className="na-howcitizenshipacquired"
            label={i18n.t('foreign.pvq.contacts.label.idk')}
            or={i18n.t('foreign.pvq.contacts.para.or')}
            onUpdate={this.updateHowCitizenshipAcquiredNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.HowCitizenshipAcquiredNotApplicable`}
          >
            <Text
              {...this.props.HowCitizenshipAcquired}
              name="HowCitizenshipAcquired"
              className="how-citizenship-acquired"
              onUpdate={this.updateHowCitizenshipAcquired}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.HowCitizenshipAcquired`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.pvq.contacts.heading.birthdate')}
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Birthdate`}
          exactAttribute
        >
          <NotApplicable
            {...this.props.BirthdateNotApplicable}
            name="BirthdateNotApplicable"
            className="na-birthdate"
            label={i18n.t('foreign.pvq.contacts.label.idk')}
            or={i18n.t('foreign.pvq.contacts.para.or')}
            onUpdate={this.updateBirthdateNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.BirthdateNotApplicable`}
          >
            <DateControl
              {...this.props.Birthdate}
              name="Birthdate"
              prefix="foreignBirthdate"
              relationship="Other"
              onUpdate={this.updateBirthdate}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Birthdate`}
            />
          </NotApplicable>
        </Field>

        <Field
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Birthplace`}
        >
          <PVQLocationUnknown
            {...birthplaceAddress}
            addressUnknown={this.props.BirthplaceNotApplicable}
            addressUnknownLabel={i18n.t('foreign.pvq.contacts.label.idk')}
            onUpdateAddressUnknown={this.updateBirthplaceNotApplicable}
            name="Birthplace"
            className="birthplace"
            layout={Location.ADDRESS}
            addressBooks={this.props.addressBooks}
            addressBook="ForeignNational"
            dispatch={this.props.dispatch}
            onUpdate={this.updateBirthplace}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Birthplace`}
            excludeUnitedStates
            showAddressUnknown
            showCityUnknown
            disableToggle
            isPoBoxAllowed={false}
            label={i18n.t('foreign.pvq.contacts.heading.birthplace')}
            domesticTitle={i18n.t('foreign.pvq.contacts.heading.birthplace')}
            foreignTitle={i18n.t('foreign.pvq.contacts.heading.birthplace')}
          />
        </Field>

        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
          adjustFor="address"
          exactAttribute
        >
          <PVQLocationUnknown
            {...this.props.Address}
            addressUnknown={this.props.AddressNotApplicable}
            addressUnknownLabel={i18n.t('foreign.pvq.contacts.label.idk')}
            inUsaUnknown={this.props.InUsaNotApplicable}
            inUsaUnknownLabel={i18n.t('foreign.pvq.contacts.label.idk')}
            attribute={`${attribute}.Address`}
            name="foreign-national-address"
            onUpdate={this.updateAddress} // eslint-disable-line @typescript-eslint/unbound-method
            onUpdateAddressUnknown={this.updateAddressNotApplicablePvq}
            onUpdateInUsaUnknown={this.updateInUsaNotApplicable}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            required={this.props.required}
            geocode
            showInUsaUnknown
            showAddressUnknown
            showCityUnknown
            showCountryNotApplicable
            showInternationalStreet
            showIsInstallationUnknownIfDomestic
            showIsInstallationUnknownIfInternational
            hideInstallationNameUnknownWhenForeign
            showMilitaryInstallation
            showStateUnknown
            showStreetUnknown
            showZipcodeUnknown
            showApoFpoUnknown
            label={i18n.t(`foreign.pvq.contacts.heading.address`)}
            domesticTitle={i18n.t(`foreign.pvq.contacts.heading.addressDomesticTitle`)}
            foreignTitle={i18n.t(`foreign.pvq.contacts.heading.addressForeignTitle`)}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.contacts.heading.relationship')}
          className="relationship-pvq"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.RelationshipPvq`}
          exactAttribute
        >
          <RadioGroup
            className="relationship option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={this.props.RelationshipPvq?.value}
            attribute={`${attribute}.RelationshipPvq`}
          >
            {foreignContactRelationshipOptions.map((option) => (
              <Radio
                key={option}
                name={option}
                label={option}
                value={option}
                className={option}
                onUpdate={this.updateRelationshipPvq}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.RelationshipPvq?.value === 'Other'
          && (
            <Field
              title={i18n.t('foreign.pvq.contacts.heading.explanation')}
              titleSize="label"
              adjustFor="textarea"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.RelationshipExplanation`}
              exactAttribute
            >
              <Textarea
                {...this.props.RelationshipExplanation}
                name="RelationshipExplanation"
                className="relationship-explanation"
                onUpdate={this.updateRelationshipExplanation}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.RelationshipExplanation`}
              />
            </Field>
          )}

        <Field
          title={i18n.t('foreign.pvq.contacts.heading.firstcontact')}
          help="foreign.pvq.contacts.help.firstcontact"
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.FirstContact`}
          exactAttribute
        >
          <DateControl
            {...this.props.FirstContact}
            name="FirstContact"
            className="first-contact"
            minDateEqualTo
            prefix="foreignContact"
            minDate={foreignContactMinDate}
            onUpdate={(value: ForeignContactsItemModel['FirstContact']) => {
              this.updateField('FirstContact', value);
            }}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            attribute={`${attribute}.FirstContact`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.contacts.heading.lastcontact')}
          help="foreign.contacts.help.lastcontact"
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.LastContact`}
          exactAttribute
        >
          <DateControl
            {...this.props.LastContact}
            name="LastContact"
            className="last-contact"
            prefix="contact.last"
            minDate={this.props.FirstContact}
            minDateEqualTo
            onUpdate={(value: ForeignContactsItemModel['LastContact']) => {
              this.updateField('LastContact', value);
            }}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            attribute={`${attribute}.LastContact`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.contacts.heading.methods')}
          className="foreign-contact-methods"
          help="foreign.pvq.contacts.help.methods"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Methods`}
          exactAttribute
        >
          <CheckboxGroup
            className="methods option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={this.props.Methods.values}
            attribute={`${attribute}.Methods`}
          >
            {foreignContactMethodOptions.map((option) => (
              <Checkbox
                key={option}
                name={option}
                label={option}
                value={option}
                className={option}
                onUpdate={this.updateMethods}
                onError={this.props.onError}
              />
            ))}
          </CheckboxGroup>
        </Field>

        {(this.props.Methods?.values || []).includes('Other') && (
          <Field
            title={i18n.t('foreign.pvq.contacts.heading.explanation')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.MethodsExplanation`}
            exactAttribute
          >
            <Textarea
              {...this.props.MethodsExplanation}
              name="MethodsExplanation"
              className="methods-explanation"
              onUpdate={(value: ForeignContactsItemModel['MethodsExplanation']) => {
                this.updateField('MethodsExplanation', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.MethodsExplanation`}
            />
          </Field>
        )}

        <Field
          attribute={`${attribute}.Email`}
          title={i18n.t('foreign.pvq.contacts.heading.email')}
          titleSize="h4"
          adjustFor="label"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          exactAttribute
        >
          <NotApplicable
            {...this.props.EmailNotApplicable}
            attribute={`${attribute}.EmailNotApplicable`}
            name="EmailNotApplicable"
            className="foreign-contact-email-na"
            label={i18n.t('foreign.pvq.contacts.label.idk')}
            or={i18n.m('foreign.pvq.contacts.para.or')}
            onUpdate={this.updateEmailNotApplicable}
            onError={this.props.onError}
          >
            <PVQEmail
              {...this.props.Email}
              attribute={`${attribute}.Email`}
              name="Email"
              className="text foreign-contact-email"
              onUpdate={this.updateEmail}
              onError={this.props.onError}
              required={
                (this.props.EmailNotApplicable || {}).applicable === false
                  ? false
                  : this.props.required
              }
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.pvq.contacts.heading.telephone')}
          className="foreign-contact-telephone override-required"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="telephone"
          attribute={`${attribute}.Telephone`}
          exactAttribute
        >
          <Telephone
            {...this.props.Telephone}
            name="Telephone"
            showTimeOfDay
            showNumberType
            onUpdate={this.updateTelephone}
            onError={this.props.onError}
            required={this.props.required}
            allowNotApplicable
            attribute={`${attribute}.Telephone`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.contacts.heading.frequency')}
          className="foreign-contact-frequency"
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Frequency`}
          exactAttribute
        >
          <RadioGroup
            className="frequency option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Frequency || {}).value}
            attribute={`${attribute}.Frequency`}
          >
            {foreignContactFrequencyOptions.map((option) => (
              <Radio
                key={option}
                name={option}
                label={option}
                value={option}
                className={option}
                onUpdate={this.updateFrequency}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.Frequency?.value === 'Other'
          && (
            <Field
              title={i18n.t('foreign.pvq.contacts.heading.explanation')}
              titleSize="label"
              adjustFor="textarea"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.FrequencyExplanation`}
              exactAttribute
            >
              <Textarea
                {...this.props.FrequencyExplanation}
                name="FrequencyExplanation"
                className="frequency-explanation"
                onUpdate={(value: ForeignContactsItemModel['FrequencyExplanation']) => {
                  this.updateField('FrequencyExplanation', value);
                }}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.FrequencyExplanation`}
              />
            </Field>
          )}

        <Field
          title={i18n.t('foreign.pvq.contacts.heading.employer')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Employer`}
          exactAttribute
        >
          <NotApplicable
            {...this.props.EmployerNotApplicable}
            name="EmployerNotApplicable"
            className="na-employer"
            label={i18n.t('foreign.pvq.contacts.label.idk')}
            or={i18n.t('foreign.pvq.contacts.para.or')}
            onUpdate={this.updateEmployerNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.EmployerNotApplicable`}
          >
            <Text
              {...this.props.Employer}
              name="Employer"
              className="employer"
              onUpdate={this.updateEmployer}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Employer`}
            />
            <Checkbox
              label={i18n.t('foreign.pvq.contacts.heading.neverWorked')}
              value="Yes"
              checked={this.props.NeverWorked?.checked}
              className="NeverWorked"
              onUpdate={this.updateNeverWorked}
              onError={this.props.onError}
            />
          </NotApplicable>
        </Field>

        {
          !(this.props.EmployerNotApplicable?.applicable === false || this.props.NeverWorked?.checked === true) && (
            <>
              <Field
                title={i18n.t('foreign.pvq.contacts.heading.occupation')}
                adjustFor="textarea"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.Occupation`}
                exactAttribute
              >
                <NotApplicable
                  {...this.props.OccupationNotApplicable}
                  name="OccupationNotApplicable"
                  className="na-occupation"
                  label={i18n.t('foreign.pvq.contacts.label.idk')}
                  or={i18n.t('foreign.pvq.contacts.para.or')}
                  onUpdate={this.updateOccupationNotApplicable}
                  required={this.props.required}
                  onError={this.props.onError}
                  attribute={`${attribute}.OccupationNotApplicable`}
                >
                  <Text
                    {...this.props.Occupation}
                    name="Occupation"
                    className="occupation"
                    onUpdate={this.updateOccupation}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.Occupation`}
                  />
                </NotApplicable>
              </Field>

              <Field
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.EmployerAddress`}
                adjustFor="address"
              >
                <PVQLocationUnknown
                  {...this.props.EmployerAddress}
                  addressUnknown={this.props.EmployerAddressNotApplicable}
                  addressUnknownLabel={i18n.t('foreign.pvq.contacts.label.idk')}
                  attribute={`${attribute}.EmployerAddress`}
                  name="foreign-national-employer-address"
                  onUpdate={this.updateEmployerAddress} // eslint-disable-line @typescript-eslint/unbound-method
                  onUpdateAddressUnknown={this.updateEmployerAddressNotApplicable}
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  required={this.props.required}
                  geocode
                  showAddressUnknown
                  showCityUnknown={isSafeInternational(this.props.EmployerAddress)}
                  showCountryNotApplicable={isSafeInternational(this.props.EmployerAddress)}
                  showInternationalStreet
                  showInternationalStreetUnknown
                  hideInstallationNameUnknownWhenForeign
                  showIsInstallationUnknownIfDomestic
                  showIsInstallationUnknownIfInternational
                  showMilitaryInstallation
                  showApoFpoUnknown={isSafeInternational(this.props.EmployerAddress) && this.props.EmployerAddress?.isMilitaryDiplomaticAddress?.value === 'Yes'}
                  label={i18n.t(`foreign.pvq.contacts.heading.employerAddress`)}
                  domesticTitle={i18n.t(`foreign.pvq.contacts.heading.employerAddressDomesticTitle`)}
                  foreignTitle={i18n.t(`foreign.pvq.contacts.heading.employerAddressForeignTitle`)}
                  internationalStreetLabel={i18n.t('address.pvq.international.street.label')}
                />
              </Field>
            </>
          )
        }
        <Field
          title={i18n.t('foreign.pvq.contacts.heading.foreignDisciplines')}
          adjustFor="buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ForeignDisciplines`}
          exactAttribute
        >
          <NotApplicable
            {...this.props.ForeignDisciplinesNotApplicable}
            name="ForeignDisciplinesNotApplicable"
            className="foreign-disciplines-not-applicable"
            onUpdate={this.updateForeignDisciplinesNotApplicable}
            onError={this.props.onError}
            or={i18n.m('foreign.pvq.contacts.para.or')}
            label={i18n.t('foreign.pvq.contacts.label.idk')}
            required={this.props.required}
            attribute={`${attribute}.ForeignDisciplinesNotApplicable`}
            includeNAConfirmation={Boolean(this.props.ForeignDisciplines?.value)}
          >
            <CheckboxGroup
              className="foreign-disciplines option-list option-list-vertical"
              onError={this.props.onError}
              required={this.props.required}
              selectedValues={this.props.ForeignDisciplines?.values}
              attribute={`${attribute}.ForeignDisciplines`}
            >
              {foreignNationalWorkOptions.map((option) => (
                <Checkbox
                  key={option}
                  name={option}
                  label={option}
                  value={option}
                  className={option}
                  onUpdate={this.updateForeignDisciplines}
                  onError={this.props.onError}
                />
              ))}
            </CheckboxGroup>
          </NotApplicable>
        </Field>

        {(this.props.ForeignDisciplines?.values?.includes('Other Government Agency')) && (
          <Field
            title={i18n.t('foreign.pvq.contacts.heading.explanation')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ForeignDisciplinesOtherExplanation`}
            exactAttribute
          >
            <Textarea
              {...this.props.ForeignDisciplinesOtherExplanation}
              name="ForeignDisciplinesOtherExplanation"
              className="foreign-disciplines-other-explanation"
              onUpdate={this.updateForeignDisciplinesOtherExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.ForeignDisciplinesOtherExplanation`}
            />
          </Field>
        )}

        {(this.props.ForeignDisciplines?.values?.includes('Military or Armed Forces')
          || this.props.ForeignDisciplines?.values?.includes('Security Service')
          || this.props.ForeignDisciplines?.values?.includes('Intelligence Service')
          || this.props.ForeignDisciplines?.values?.includes('Defense Industry')
          || this.props.ForeignDisciplines?.values?.includes('Foreign Movement (such as a social, political, or religious movement)')) && (
            <Field
              title={i18n.t('foreign.pvq.contacts.heading.foreignAssociationDescription')}
              titleSize="label"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ForeignAssociationDescription`}
              exactAttribute
            >
              <NotApplicable
                {...this.props.ForeignAssociationDescriptionNotApplicable}
                name="ForeignAssociationDescriptionNotApplicable"
                className="foreign-association-description-not-applicable"
                onUpdate={this.updateForeignAssociationDescriptionNotApplicable}
                onError={this.props.onError}
                or={i18n.m('foreign.pvq.contacts.para.or')}
                label={i18n.t('foreign.pvq.contacts.label.idk')}
                required={this.props.required}
                attribute={`${attribute}.ForeignAssociationDescriptionNotApplicable`}
                includeNAConfirmation={Boolean(this.props.ForeignAssociationDescription?.value)}
              >
                <Textarea
                  {...this.props.ForeignAssociationDescription}
                  name="ForeignAssociationDescription"
                  className="foreign-disciplines-other-explanation"
                  onUpdate={this.updateForeignAssociationDescription}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.ForeignAssociationDescription`}
                />
              </NotApplicable>
            </Field>
        )}

        <Branch
          {...this.props.HasObligations}
          name="HasObligations"
          attribute={`${attribute}.HasObligations`}
          label={i18n.t('foreign.pvq.contacts.heading.hasObligations')}
          labelSize="h4"
          className="has-obligations"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateHasObligations}
          scrollIntoView={this.props.scrollIntoView}
          exactAttribute
        />

        {this.props.HasObligations?.value === 'Yes' && (
          <Field
            title={i18n.t('foreign.pvq.contacts.heading.explanation')}
            attribute={`${attribute}.Obligations`}
            scrollIntoView={this.props.scrollIntoView}
            exactAttribute
          >
            <Textarea
              {...this.props.Obligations}
              name={`${attribute}.Obligations`}
              attribute="Obligations"
              className="obligations"
              onUpdate={this.updateObligations}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}
      </div>
    ) : (
      <div className="foreign-national">
        <Field
          title={i18n.t('foreign.contacts.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          className={classNames('foreign-national-name', {
            'no-margin-bottom': !this.props.NameNotApplicable?.applicable,
          })}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Name`}
        >
          <NotApplicable
            name="NameNotApplicable"
            className="na-name"
            label={i18n.t('foreign.contacts.label.idkname')}
            or={i18n.t('foreign.contacts.para.or')}
            {...this.props.NameNotApplicable}
            onUpdate={this.updateNameNotApplicable}
            onError={this.props.onError}
          >
            <Name
              name="Name"
              {...this.props.Name}
              onUpdate={(value: ForeignContactsItemModel['Name']) => {
                this.updateField('Name', value);
              }}
              aria-label="Provide the name of the individual involved in the contact"
              onError={this.handleNameError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Name`}
            />
          </NotApplicable>
        </Field>

        <Show when={this.props.NameNotApplicable?.applicable === false}>
          <Field
            title={i18n.t('foreign.contacts.heading.explanation')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.NameExplanation`}
          >
            <Textarea
              name="NameExplanation"
              className="name-explanation"
              {...this.props.NameExplanation}
              onUpdate={this.updateNameExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.NameExplanation`}
            />
          </Field>
        </Show>

        <Field
          title={i18n.t('foreign.contacts.heading.birthdate')}
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Birthdate`}
        >
          <NotApplicable
            name="BirthdateNotApplicable"
            className="na-birthdate"
            label={i18n.t('foreign.contacts.label.idk')}
            or={i18n.t('foreign.contacts.para.or')}
            {...this.props.BirthdateNotApplicable}
            onUpdate={this.updateBirthdateNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.BirthdateNotApplicable`}
          >
            <DateControl
              name="Birthdate"
              prefix="foreignBirthdate"
              {...this.props.Birthdate}
              relationship="Other"
              onUpdate={(value: ForeignContactsItemModel['Birthdate']) => {
                this.updateField('Birthdate', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Birthdate`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.contacts.heading.firstcontact')}
          help="foreign.contacts.help.firstcontact"
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.FirstContact`}
        >
          <DateControl
            name="FirstContact"
            className="first-contact"
            minDateEqualTo
            prefix="foreignContact"
            minDate={foreignContactMinDate}
            {...this.props.FirstContact}
            onUpdate={(value: ForeignContactsItemModel['FirstContact']) => {
              this.updateField('FirstContact', value);
            }}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            attribute={`${attribute}.FirstContact`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.contacts.heading.lastcontact')}
          help="foreign.contacts.help.lastcontact"
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.LastContact`}
        >
          <DateControl
            name="LastContact"
            className="last-contact"
            {...this.props.LastContact}
            prefix="contact.last"
            minDate={this.props.FirstContact}
            minDateEqualTo
            onUpdate={(value: ForeignContactsItemModel['LastContact']) => {
              this.updateField('LastContact', value);
            }}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            attribute={`${attribute}.LastContact`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.contacts.heading.methods')}
          className={
            methodsPropIncludesOther
              ? 'no-margin-bottom'
              : ''
          }
          help="foreign.contacts.help.methods"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Methods`}
        >
          {i18n.t('foreign.contacts.para.checkall')}
          <CheckboxGroup
            className="methods option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={Methods.values}
            attribute={`${attribute}.Methods`}
          >
            <Checkbox
              name="methods-inperson"
              label={i18n.t('foreign.contacts.label.inperson')}
              value="In person"
              className="methods-inperson"
              onUpdate={this.updateMethods}
              onError={this.props.onError}
            />
            <Checkbox
              name="methods-telephone"
              label={i18n.t('foreign.contacts.label.telephone')}
              value="Telephone"
              className="methods-telephone"
              onUpdate={this.updateMethods}
              onError={this.props.onError}
            />
            <Checkbox
              name="methods-electronic"
              label={i18n.t('foreign.contacts.label.electronic')}
              value="Electronic"
              className="methods-electronic"
              onUpdate={this.updateMethods}
              onError={this.props.onError}
            />
            <Checkbox
              name="methods-written"
              label={i18n.t('foreign.contacts.label.written')}
              value="Written"
              className="methods-written"
              onUpdate={this.updateMethods}
              onError={this.props.onError}
            />
            <Checkbox
              name="methods-other"
              label={i18n.t('foreign.contacts.label.other')}
              value="Other"
              className="methods-other"
              onUpdate={this.updateMethods}
              onError={this.props.onError}
            />
          </CheckboxGroup>
        </Field>

        <Show when={methodsPropIncludesOther}>
          <Field
            title={i18n.t('foreign.contacts.heading.explanation')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.MethodsExplanation`}
          >
            <Textarea
              name="MethodsExplanation"
              className="methods-explanation"
              {...this.props.MethodsExplanation}
              onUpdate={(value: ForeignContactsItemModel['MethodsExplanation']) => {
                this.updateField('MethodsExplanation', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.MethodsExplanation`}
            />
          </Field>
        </Show>

        <Field
          title={i18n.t('foreign.contacts.heading.frequency')}
          className={(this.props.Frequency || {}).value === 'Other' ? 'no-margin-bottom' : ''}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Frequency`}
        >
          <RadioGroup
            className="frequency option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Frequency || {}).value}
            attribute={`${attribute}.Frequency`}
          >
            <Radio
              name="frequency-daily"
              label={i18n.t('foreign.contacts.label.daily')}
              value="Daily"
              className="frequency-daily"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="frequency-weekly"
              label={i18n.t('foreign.contacts.label.weekly')}
              value="Weekly"
              className="frequency-weekly"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="frequency-monthly"
              label={i18n.t('foreign.contacts.label.monthly')}
              value="Monthly"
              className="frequency-monthly"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="frequency-quarterly"
              label={i18n.t('foreign.contacts.label.quarterly')}
              value="Quarterly"
              className="frequency-quarterly"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="frequency-annually"
              label={i18n.t('foreign.contacts.label.annually')}
              value="Annually"
              className="frequency-annually"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="frequency-other"
              label={i18n.t('foreign.contacts.label.other')}
              value="Other"
              className="frequency-other"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Show when={(this.props.Frequency || {}).value === 'Other'}>
          <Field
            title={i18n.t('foreign.contacts.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.FrequencyExplanation`}
          >
            <Textarea
              name="FrequencyExplanation"
              className="frequency-explanation"
              {...this.props.FrequencyExplanation}
              onUpdate={(value: ForeignContactsItemModel['FrequencyExplanation']) => {
                this.updateField('FrequencyExplanation', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.FrequencyExplanation`}
            />
          </Field>
        </Show>

        <Field
          title={i18n.t('foreign.contacts.heading.relationship')}
          className={
            ((this.props.Relationship || {}).values || []).some(
              (x) => x === 'Other' || x === 'Obligation'
            )
              ? 'no-margin-bottom'
              : ''
          }
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Relationship`}
        >
          {i18n.t('foreign.contacts.para.checkall')}
          <CheckboxGroup
            className="relationship option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValues={(this.props.Relationship || {}).values}
            attribute={`${attribute}.Relationship`}
          >
            <Checkbox
              name="relationship-professional"
              label={i18n.t('foreign.contacts.label.professional')}
              value="Professional"
              className="relationship-professional"
              onUpdate={this.updateRelationship}
              onError={this.props.onError}
            />
            <Checkbox
              name="relationship-personal"
              label={i18n.t('foreign.contacts.label.personal')}
              value="Personal"
              className="relationship-personal"
              onUpdate={this.updateRelationship}
              onError={this.props.onError}
            />
            <Checkbox
              name="relationship-obligation"
              label={i18n.t('foreign.contacts.label.obligation')}
              value="Obligation"
              className="relationship-obligation"
              onUpdate={this.updateRelationship}
              onError={this.props.onError}
            />
            <Checkbox
              name="relationship-other"
              label={i18n.t('foreign.contacts.label.other')}
              value="Other"
              className="relationship-other"
              onUpdate={this.updateRelationship}
              onError={this.props.onError}
            />
          </CheckboxGroup>
        </Field>

        <Show
          when={((this.props.Relationship || {}).values || []).some(
            (x) => x === 'Other' || x === 'Obligation'
          )}
        >
          <Field
            title={i18n.t('foreign.contacts.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.RelationshipExplanation`}
          >
            <Textarea
              name="RelationshipExplanation"
              className="relationship-explanation"
              {...this.props.RelationshipExplanation}
              onUpdate={(value: ForeignContactsItemModel['RelationshipExplanation']) => {
                this.updateField('RelationshipExplanation', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.RelationshipExplanation`}
            />
          </Field>
        </Show>

        <BranchCollection
          label={i18n.t('foreign.contacts.heading.aliases')}
          appendLabel={i18n.t('foreign.contacts.heading.aliases2')}
          help="foreign.contacts.help.aliases"
          className="aliases"
          {...this.props.Aliases}
          onUpdate={(value: ForeignContactsItemModel['Aliases']) => {
            this.updateField('Aliases', value);
          }}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Aliases`}
        >
          <ForeignNationalNameItem
            bind
            name="Item"
            handleNameError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Aliases`}
          />
        </BranchCollection>

        <Field
          title={i18n.t('foreign.contacts.heading.citizenship')}
          help="foreign.contacts.help.citizenship"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Citizenship`}
        >
          <Country
            name="Citizenship"
            multiple
            className="citizenship"
            {...this.props.Citizenship}
            onUpdate={(value: ForeignContactsItemModel['Citizenship']) => {
              this.updateField('Citizenship', value);
            }}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Citizenship`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.contacts.heading.birthplace')}
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Birthplace`}
        >
          <NotApplicable
            name="BirthplaceNotApplicable"
            className="na-birthplace"
            label={i18n.t('foreign.contacts.label.idk')}
            or={i18n.t('foreign.contacts.para.or')}
            {...this.props.BirthplaceNotApplicable}
            onUpdate={this.updateBirthplaceNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.BirthplaceNotApplicable`}
          >
            <Location
              name="Birthplace"
              className="birthplace"
              {...this.props.Birthplace}
              layout={Location.CITY_COUNTRY}
              addressBooks={this.props.addressBooks}
              addressBook="ForeignNational"
              dispatch={this.props.dispatch}
              onUpdate={(value: ForeignContactsItemModel['Birthplace']) => {
                this.updateField('Birthplace', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Birthplace`}
              excludeUnitedStates={false}
            />
          </NotApplicable>
        </Field>

        {this.props.BirthplaceNotApplicable?.applicable === false && (
          <Field
            title={i18n.t('foreign.contacts.heading.idkBirthplace')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.BirthplaceExplanation`}
          >
            <Textarea
              name="BirthplaceExplanation"
              className="birthplace-explanation"
              {...this.props.BirthplaceExplanation}
              onUpdate={this.updateBirthplaceExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.BirthplaceExplanation`}
            />
          </Field>
        )}

        <Field
          title={i18n.t('foreign.contacts.heading.address')}
          optional
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
        >
          <NotApplicable
            name="AddressNotApplicable"
            className="na-address"
            label={i18n.t('foreign.contacts.label.idk')}
            or={i18n.t('foreign.contacts.para.or')}
            {...this.props.AddressNotApplicable}
            onUpdate={this.updateAddressNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.AddressNotApplicable`}
            forceRefresh={this.state.currentAddressIdkRefresh}
            useChangeWarning
          >
            <Location
              name="Address"
              className="current-address"
              {...this.props.Address}
              layout={Location.ADDRESS}
              geocode
              addressBooks={this.props.addressBooks}
              addressBook="ForeignNational"
              dispatch={this.props.dispatch}
              onUpdate={(value: ForeignContactsItemModel['Address']) => {
                this.updateAddressField(value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Address`}
              useChangeWarning
            />
          </NotApplicable>
        </Field>
        <Show
          when={
            this.props.Address
            && this.props.Address.country
            && countryString(this.props.Address.country || {}) !== 'United States'
          }
        >
          <AlternateAddress
            address={this.props.AlternateAddress}
            addressBook="ForeignNational"
            belongingTo="AlternateAddress"
            country={countryString(this.props.Address?.country)}
            forceAPO
            militaryAddressLabel={i18n.t('address.militaryAddress.foreignNational')}
            onUpdate={this.update}
            required={this.props.required}
            attribute={`${attribute}.AlternateAddress`}
            showDNK={this.props.requireForeignContactAPOFPOIDK}
          />
        </Show>
        <Field
          title={i18n.t('foreign.contacts.heading.employer')}
          scrollIntoView={this.props.scrollIntoView}
          exactAttribute
          attribute={`${attribute}.Employer`}
        >
          <NotApplicable
            name="EmployerNotApplicable"
            className="na-employer"
            label={i18n.t('foreign.contacts.label.idk')}
            or={i18n.t('foreign.contacts.para.or')}
            {...this.props.EmployerNotApplicable}
            onUpdate={this.updateEmployerNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.EmployerNotApplicable`}
          >
            <Text
              name="Employer"
              className="employer"
              {...this.props.Employer}
              onUpdate={(value: ForeignContactsItemModel['Employer']) => {
                this.updateField('Employer', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Employer`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.contacts.heading.employeraddress')}
          optional
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.EmployerAddress`}
        >
          <NotApplicable
            name="EmployerAddressNotApplicable"
            className="na-employer-address"
            label={i18n.t('foreign.contacts.label.idk')}
            or={i18n.t('foreign.contacts.para.or')}
            {...this.props.EmployerAddressNotApplicable}
            onUpdate={this.updateEmployerAddressNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.EmployerAddressNotApplicable`}
          >
            <Location
              name="EmployerAddress"
              className="employer-address"
              {...this.props.EmployerAddress}
              layout={Location.ADDRESS}
              geocode
              addressBooks={this.props.addressBooks}
              addressBook="Company"
              showPostOffice
              dispatch={this.props.dispatch}
              onUpdate={(value: ForeignContactsItemModel['EmployerAddress']) => {
                this.updateField('EmployerAddress', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.EmployerAddress`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.contacts.heading.hasaffiliations')}
          adjustFor="buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.HasAffiliations`}
        >
          <RadioGroup
            className="has-affiliations option-list"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.HasAffiliations || {}).value}
            attribute={`${attribute}.HasAffiliations`}
          >
            <Radio
              name="affiliation_yes"
              label={i18n.t('foreign.contacts.label.yes')}
              value="Yes"
              className="yes"
              onUpdate={(value: ForeignContactsItemModel['HasAffiliations']) => {
                this.updateField('HasAffiliations', value);
              }}
              onError={this.props.onError}
            />
            <Radio
              name="affiliation_no"
              label={i18n.t('foreign.contacts.label.no')}
              value="No"
              className="no"
              onUpdate={(value: ForeignContactsItemModel['HasAffiliations']) => {
                this.updateField('HasAffiliations', value);
              }}
              onError={this.props.onError}
            />
            <Radio
              name="affiliation_idk"
              label={i18n.t('foreign.contacts.label.idk')}
              value="I don't know"
              className="idk"
              onUpdate={(value: ForeignContactsItemModel['HasAffiliations']) => {
                this.updateField('HasAffiliations', value);
              }}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Show when={(this.props.HasAffiliations || {}).value === 'Yes'}>
          <Field
            title={i18n.t('foreign.contacts.heading.affiliations')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Affiliations`}
          >
            <Textarea
              name="Affiliations"
              className="affiliations"
              {...this.props.Affiliations}
              onUpdate={(value: ForeignContactsItemModel['Affiliations']) => {
                this.updateField('Affiliations', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Affiliations`}
            />
          </Field>
        </Show>
      </div>
    );
  }
}

interface ForeignNationalNameItemProps extends AccordionItemProps, ForeignContactsItemAliasesModel {
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
  onUpdate?: CommonOnUpdate
  // Temporary. This will be removed in PI 11
  handleNameError: any
  formType?: string
}

const ForeignNationalNameItem: React.FC<ForeignNationalNameItemProps> = function ({
  scrollIntoView,
  required,
  attribute,
  handleNameError,
  $index,
  onUpdate,
  Alias,
  AliasDescription,
  AliasDescriptionOtherExplanation,
  formType
}) {
  const nameAttribute = `${attribute}.items[${$index}].Item.Alias`;
  const nameOnUpdate = (values: Partial<NameModel>) => {
    onUpdate({ Alias: values });
  };

  const aliasDescriptionOnUpdate = (values) => {
    onUpdate({
      AliasDescription: values,
      AliasDescriptionOtherExplanation: values.value === 'Other' ? (AliasDescriptionOtherExplanation || {}) : {}
    });
  };

  const aliasDescriptionOtherExplanationOnUpdate = (values) => {
    onUpdate({ AliasDescriptionOtherExplanation: values });
  };

  const isPVQ = isPVQForm(formType);

  return isPVQ ? (
    <>
      <Field
        title={i18n.t('foreign.pvq.contacts.heading.aliasname')}
        optional
        filterErrors={Name.requiredErrorsOnly}
        scrollIntoView={scrollIntoView}
        exactAttribute
      >
        <PVQName
          {...Alias}
          name="Alias"
          onUpdate={nameOnUpdate}
          aria-label="Provide the other name or nickname"
          required={required}
          scrollIntoView={scrollIntoView}
          onError={handleNameError}
          attribute={nameAttribute}
          showFirstUnknown
          showMiddleUnknown
          showLastUnknown
          exactAttribute
        />
      </Field>

      <Field
        title={i18n.t('foreign.pvq.contacts.heading.nameDescription')}
        filterErrors={Name.requiredErrorsOnly}
        className="foreign-contact-alias-description"
        scrollIntoView={scrollIntoView}
        attribute={`${attribute}.items[${$index}].Item.AliasDescription`}
        exactAttribute
      >
        <RadioGroup
          className="ordered-by option-list option-list-vertical"
          required={required}
          onError={handleNameError}
          selectedValue={AliasDescription?.value}
          attribute={`${attribute}.items[${$index}].Item.AliasDescription`}
        >
          {otherNamesOptions.map((option) => (
            <Radio
              key={option}
              name={option}
              label={option}
              value={option}
              className={option}
              onUpdate={aliasDescriptionOnUpdate}
              onError={handleNameError}
            />
          ))}
        </RadioGroup>
      </Field>

      {AliasDescription?.value === 'Other'
        && (
          <Field
            title={i18n.t('foreign.pvq.contacts.label.explain')}
            filterErrors={Name.requiredErrorsOnly}
            className="foreign-contact-alias-description-other-explanation"
            scrollIntoView={scrollIntoView}
            attribute={`${attribute}.items[${$index}].Item.AliasDescriptionOtherExplanation`}
            exactAttribute
          >
            <Textarea
              {...AliasDescriptionOtherExplanation}
              name="AliasDescriptionOtherExplanation"
              className="foreign-contact-alias-description-other"
              onUpdate={aliasDescriptionOtherExplanationOnUpdate}
              onError={handleNameError}
              required={required}
              attribute={`${attribute}.items[${$index}].Item.AliasDescriptionOtherExplanation`}
            />
          </Field>
        )}
    </>
  ) : (
    <Field
      title={i18n.t('foreign.contacts.heading.aliasname')}
      optional
      filterErrors={Name.requiredErrorsOnly}
      scrollIntoView={scrollIntoView}
    >
      <Name
        name="Alias"
        onUpdate={nameOnUpdate}
        aria-label="Provide the other name or nickname"
        {...Alias}
        required={required}
        scrollIntoView={scrollIntoView}
        onError={handleNameError}
        attribute={nameAttribute}
      />
    </Field>
  );
};

ForeignNational.defaultProps = {
  Name: {},
  NameNotApplicable: {
    applicable: true,
  },
  NameExplanation: {},
  FirstContact: {},
  LastContact: {},
  Methods: {},
  MethodsNotApplicable: { applicable: true },
  MethodsExplanation: {},
  Frequency: {},
  FrequencyExplanation: {},
  Relationship: {},
  RelationshipExplanation: {},
  Aliases: {},
  Citizenship: {},
  Birthdate: {},
  BirthdateNotApplicable: { applicable: true },
  Birthplace: {},
  BirthplaceNotApplicable: { applicable: true },
  Address: {},
  AddressNotApplicable: { applicable: true },
  Employer: {},
  EmployerNotApplicable: { applicable: true },
  EmployerAddress: {},
  EmployerAddressNotApplicable: { applicable: true },
  BirthplaceExplanation: {},
  OrganizationNotApplicable: { applicable: true },
  OrganizationAddressNotApplicable: { applicable: true },
  HasAffiliations: {},
  Affiliations: {},
  HowCitizenshipAcquired: {}, // PVQ Only
  HowCitizenshipAcquiredNotApplicable: { applicable: true }, // PVQ Only
  RelationshipPvq: {}, // PVQ Only
  Email: {}, // PVQ Only
  EmailNotApplicable: { applicable: true }, // PVQ Only
  Telephone: {}, // PVQ Only
  Occupation: {}, // PVQ Only
  OccupationNotApplicable: { applicable: true }, // PVQ Only
  ForeignDisciplines: {}, // PVQ Only
  ForeignDisciplinesOtherExplanation: {}, // PVQ Only
  ForeignDisciplinesNotApplicable: { applicable: true }, // PVQ Only
  ForeignAssociationDescription: {}, // PVQ Only
  ForeignAssociationDescriptionNotApplicable: { applicable: true }, // PVQ Only
  HasObligations: {}, // PVQ Only
  Obligations: {}, // PVQ Only
  addressBooks: {},
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
